import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { TruncatedTextTooltip } from "@/components/ui/truncated-text-tooltip";
import InfoTooltip from "@/components/ui/InfoTooltip";
import { WidgetTitle } from "@/components/widgets/WidgetTitle";
import SearchInput from "@/components/ui/SearchInput";
import { Button } from "@/components/ui/button";
import { Download, ChevronDown, ChevronUp, ChevronRight, RefreshCcw, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { convertLocalTimezone } from "@/utils/dateUtils";
import { TablePagingFooter } from "@/components/widgets/common/TablePagingFooter";
import { fetchQueueCallJourneyTable, fetchQueueCallJourneyFilter, DirectCallJourneyRow } from "@/lib/axios/api/widgets.api";
import { formatDuration, formatDirectionForDisplay } from "@/components/widgets/users/tableCellHelpers";
import { useColumnFilterState, ColFilterPopover } from "@/components/widgets/users/columnFilterShared";
import Timeline from "@/components/widgets/call-overview/Timeline";
import { getPresenceColor, getDirectionColor } from "@/components/widgets/call-overview/callStatusColors";
import CollapseToggleButton from "@/components/widgets/common/CollapseToggleButton";
import { QueueCallVolumeStatus } from "@/components/widgets/call-overview/QueueVolumeWidget";
import {
  TableGridHeaderRow,
  TableGridHeaderCell,
  TableGridRow,
  TableGridCell,
  TABLE_HEADER_ROW_COLOR_CLASS,
  TABLE_HEADER_TITLE_CLASS,
  type TableColumnConfig,
} from "@/components/ui/table";
import * as XLSX from "xlsx";

const QUEUE_CALL_VOLUME_STATUS_LABEL: Record<QueueCallVolumeStatus, string> = {
  answered: "Answered",
  missed: "Missed",
  transferred: "Transferred",
  abandoned: "Abandoned",
};

/** Reuses the same call-status tokens as callStatusColors' getPresenceColor. */
const QUEUE_CALL_VOLUME_STATUS_COLOR_CLASS: Record<QueueCallVolumeStatus, string> = {
  answered: "border-call-status-answered/40 bg-call-status-answered/10 text-call-status-answered",
  missed: "border-call-status-missed/40 bg-call-status-missed/10 text-call-status-missed",
  transferred: "border-call-status-transferred/40 bg-call-status-transferred/10 text-call-status-transferred",
  abandoned: "border-call-status-abandoned/40 bg-call-status-abandoned/10 text-call-status-abandoned",
};

// TCX-6287 Queue tab: per-queue Call Journey table, ported from teamscx-realtime-report's
// QueueCallJourneyWidget onto POST /queue/table (tableId=1) + POST /queue/filter (filterId=1),
// mirroring DirectCallJourneyTableWidget's sort/column-filter/expand/pagination architecture.
const SEARCH_MAIN_COLUMN = "agentName";
const FILTER_PAGE_SIZE = 50;
const ROW_HEIGHT_CLASS = "h-8";

function capitalizeFirst(value: string): string {
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : value;
}

function buildColumnFiltersPayload(cf: Record<string, string[]>) {
  const out = Object.entries(cf)
    .filter(([, values]) => values.length > 0)
    .map(([column, values]) => ({ column, values }));
  return out.length ? out : null;
}

function isAbortError(e: unknown): boolean {
  return e instanceof Error && (e.name === "CanceledError" || e.name === "AbortError");
}

const BASE_COLUMNS: TableColumnConfig[] = [
  { title: "Start Time", key: "startTime", width: 170, filterable: true },
  { title: "Organizer", key: "organizer", width: 160, filterable: true },
  { title: "Participant", key: "participant", width: 220, filterable: true },
  { title: "Direction", key: "direction", width: 130, filterable: true },
  { title: "Call Status", key: "callStatus", width: 120, filterable: true },
  { title: "Call Duration", key: "callDuration", width: 100, filterable: true },
  { title: "Customer Name", key: "customerName", width: 160, filterable: true },
  { title: "Company Name", key: "companyName", width: 160, filterable: true },
  { title: "Voicemail", key: "voicemail", width: 100, filterable: true },
];

/** TCX-6406: Customer Name / Company Name are gated behind the backend's runtime toggle. */
const CUSTOMER_COMPANY_NAME_KEYS = new Set(["customerName", "companyName"]);

const FILTERABLE_COLUMN_KEYS = BASE_COLUMNS.filter((c) => c.filterable).map((c) => c.key);

// Export order differs from the on-screen table: Waiting/Talking Duration are inserted right
// before Call Duration, with Voicemail last.
const EXPORT_COLUMNS = [
  { title: "Start Time", key: "startTime" },
  { title: "Organizer", key: "organizer" },
  { title: "Participant", key: "participant" },
  { title: "Direction", key: "direction" },
  { title: "Call Status", key: "callStatus" },
  { title: "Waiting Duration", key: "waitingDuration" },
  { title: "Talking Duration", key: "talkingDuration" },
  { title: "Call Duration", key: "callDuration" },
  // TCX-6406 Amendment: Customer Name / Company Name are shown on-screen but excluded from export.
  { title: "Voicemail", key: "voicemail" },
];

// The filter endpoint only understands single-value `{ column, value }` clauses — no `values`
// arrays. A column with multiple selected values is joined into one comma-separated string
// (matches the convention already used for /user/filter's appliedApiFilters), so a multi-select
// on another column still narrows this column's distinct list instead of being dropped. Every
// other filterable column is always included — `value: null` for columns with no active filter —
// so the backend knows the full column set, not just the ones currently narrowed.
function getOtherColumnFilters(columnFilters: Record<string, string[]>, col: string) {
  const out = FILTERABLE_COLUMN_KEYS
    .filter((k) => k !== col)
    .map((column) => {
      const values = columnFilters[column] ?? [];
      return { column, value: values.length > 0 ? values.join(', ') : null };
    });
  return out.length ? out : null;
}

interface QueueCallJourneyTableWidgetProps {
  queueMicrosoftId: string;
  queueName: string;
  startDate: string;
  endDate: string;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  /** TCX-4302: scopes the table to these call ids (set when a Call Volume stat tile is clicked). */
  callIds?: string[];
  /** Active Call Volume drill-down status, rendered as a clearable chip in the footer. */
  drilldownStatus?: QueueCallVolumeStatus;
  onClearDrilldown?: () => void;
  // Bumped by a with-agents-and-role poll detecting a real change (e.g. an agent renamed) —
  // this table's own query identity (queue/date/sort/filters) never touches agent names, so
  // without this the Organizer/Participant columns keep showing the stale name.
  queuesDataVersion?: number;
}

const QueueCallJourneyTableWidget: React.FC<QueueCallJourneyTableWidgetProps> = ({
  queueMicrosoftId,
  queueName,
  startDate,
  endDate,
  collapsed = false,
  onToggleCollapse,
  callIds,
  drilldownStatus,
  onClearDrilldown,
  queuesDataVersion = 0,
}) => {
  const [sortKey, setSortKey] = useState<string>("startTime");
  const [sortDir, setSortDir] = useState<"Asc" | "Desc">("Desc");
  const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>({});
  const [tableData, setTableData] = useState<DirectCallJourneyRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [customerCompanyNameEnabled, setCustomerCompanyNameEnabled] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [exportLoading, setExportLoading] = useState(false);
  const filterParamsKeyRef = React.useRef<Record<string, string>>({});
  const skeletonRowIds = useMemo(() => Array.from({ length: limit }, () => crypto.randomUUID()), [limit]);
  const columns = useMemo(
    () =>
      (customerCompanyNameEnabled ? BASE_COLUMNS : BASE_COLUMNS.filter((c) => !CUSTOMER_COMPANY_NAME_KEYS.has(c.key))).filter(
        (c) => c.key !== "callStatus"
      ),
    [customerCompanyNameEnabled]
  );
  const colFilter = useColumnFilterState();

  // ── Table data fetch ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!queueMicrosoftId || !startDate || !endDate) {
      setTableData([]);
      return;
    }
    const controller = new AbortController();
    let cancelled = false;
    const mainFilters = search ? [{ column: SEARCH_MAIN_COLUMN, value: search }] : null;

    setLoading(true);
    setError("");
    fetchQueueCallJourneyTable(
      {
        startTime: startDate,
        endTime: endDate,
        queueMicrosoftId,
        sorts: sortKey ? [{ column: sortKey, direction: sortDir }] : null,
        pageIndex: page - 1,
        pageSize: limit,
        filters: buildColumnFiltersPayload(columnFilters),
        mainFilters,
        callIds,
      },
      { signal: controller.signal }
    )
      .then((result) => {
        if (cancelled) return;
        setTableData(result.data);
        setTotal(result.total);
        setCustomerCompanyNameEnabled(result.customerCompanyNameEnabled);
      })
      .catch((e) => {
        if (cancelled || isAbortError(e)) return;
        setError(e instanceof Error ? e.message : "Error loading data");
        setTableData([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [queueMicrosoftId, sortKey, sortDir, startDate, endDate, page, limit, columnFilters, search, callIds, queuesDataVersion]);

  // Reset page on sort / filter / search / drill-down change
  useEffect(() => {
    setPage(1);
  }, [sortKey, sortDir, columnFilters, search, callIds]);

  // Reset column filter state and expanded rows when queue/date scope or drill-down changes,
  // or a poll detects a real agent/queue change (e.g. a rename) — otherwise the filter
  // dropdowns keep serving cached stale names/ids even after the table itself refetches.
  useEffect(() => {
    colFilter.reset();
    filterParamsKeyRef.current = {};
    setExpandedRows(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queueMicrosoftId, startDate, endDate, callIds, queuesDataVersion]);

  const buildFilterParamsKey = useCallback(
    (col: string) => {
      const otherFilters = getOtherColumnFilters(columnFilters, col);
      return `${queueMicrosoftId}|${startDate}|${endDate}|${search}|${(callIds ?? []).join(",")}|${JSON.stringify(otherFilters)}`;
    },
    [queueMicrosoftId, startDate, endDate, search, columnFilters, callIds]
  );

  // ── Column filter fetch helpers ───────────────────────────────────────────
  const fetchFilterPage = useCallback(
    async (col: string, pageIndex: number, append: boolean, dropdownSearch: string) => {
      if (!queueMicrosoftId || !startDate || !endDate) return;
      colFilter.abortCol(col);
      const ac = new AbortController();
      colFilter.abortRefs.current[col] = ac;

      const otherFilters = getOtherColumnFilters(columnFilters, col);
      // Target column: `value` carries the dropdown's search text so the backend narrows
      // its distinct list server-side; null means no narrowing (full list).
      const filters = [...(otherFilters ?? []), { column: col, value: dropdownSearch.trim() || null }];
      const mainFilters = search ? [{ column: SEARCH_MAIN_COLUMN, value: search }] : null;
      try {
        const res = await fetchQueueCallJourneyFilter(
          {
            startTime: startDate,
            endTime: endDate,
            queueMicrosoftId,
            targetColumn: col,
            pageIndex,
            pageSize: FILTER_PAGE_SIZE,
            mainFilters,
            filters,
            callIds,
          },
          { signal: ac.signal }
        );
        if (ac.signal.aborted) return;
        // Computed synchronously (not inside setEntry's functional updater) so hasMoreAfter
        // is reliable immediately below — the updater form only runs once React flushes the
        // state update, which can land after this function has already moved on.
        const priorOptions = append ? colFilter.getEntry(col).options : [];
        const merged = [...new Set([...priorOptions, ...res.values])];
        // Also stop when this page added nothing new: `total` is the backend's count and may never be
        // reachable by the de-duplicated list (rows vs distinct values, or a page that repeats), which
        // would leave `hasMore` stuck on and keep requesting pages on every scroll.
        const hasMoreAfter = merged.length > priorOptions.length && merged.length < res.total;
        colFilter.setEntry(col, {
          options: merged,
          hasMore: hasMoreAfter,
          loadedPage: pageIndex,
          loading: false,
        });
      } catch (e) {
        if (ac.signal.aborted) return;
        if (isAbortError(e)) return;
        colFilter.setEntry(col, { loading: false });
      }
    },
    [queueMicrosoftId, startDate, endDate, search, columnFilters, callIds, colFilter]
  );

  const handleFilterOpen = useCallback(
    (col: string) => {
      const entry = colFilter.getEntry(col);
      const paramsKey = buildFilterParamsKey(col);
      if (entry.loadedPage >= 0 && filterParamsKeyRef.current[col] === paramsKey) {
        return;
      }
      filterParamsKeyRef.current[col] = paramsKey;
      colFilter.setEntry(col, { loading: true, searchQuery: "", loadedPage: -1, options: [], hasMore: false });
      fetchFilterPage(col, 0, false, "");
    },
    [colFilter, fetchFilterPage, buildFilterParamsKey]
  );

  const handleFilterClose = useCallback((col: string) => {
    const timers = colFilter.searchTimers.current;
    if (timers[col]) {
      clearTimeout(timers[col]);
      delete timers[col];
    }
    // Options were narrowed by a dropdown search — invalidate the cache so the next
    // open refetches the full (unsearched) distinct list.
    if (colFilter.getEntry(col).searchQuery.trim()) {
      delete filterParamsKeyRef.current[col];
    }
  }, [colFilter]);

  const handleFilterSearchChange = useCallback((col: string, query: string) => {
    colFilter.setEntry(col, { searchQuery: query });
    const timers = colFilter.searchTimers.current;
    if (timers[col]) clearTimeout(timers[col]);
    timers[col] = setTimeout(() => {
      delete timers[col];
      // Do NOT flip loadedPage back to -1 here: ColFilterPopover reads a loaded→not-loaded
      // transition on an open popover as an external reset and calls onOpen(), which wipes the
      // search box and refetches the unfiltered list. Keep the previous options until the
      // searched response replaces them.
      colFilter.setEntry(col, { loading: true, hasMore: false });
      fetchFilterPage(col, 0, false, query);
    }, 400);
  }, [colFilter, fetchFilterPage]);

  const handleFilterLoadMore = useCallback(
    (col: string) => {
      const entry = colFilter.getEntry(col);
      if (!entry.hasMore || entry.loading) return;
      colFilter.setEntry(col, { loading: true });
      fetchFilterPage(col, entry.loadedPage + 1, true, entry.searchQuery);
    },
    [colFilter, fetchFilterPage]
  );

  const handleToggleFilterValue = useCallback((col: string, val: string) => {
    setColumnFilters((prev) => {
      const cur = prev[col] ?? [];
      const next = cur.includes(val) ? cur.filter((v) => v !== val) : [...cur, val];
      return next.length ? { ...prev, [col]: next } : Object.fromEntries(Object.entries(prev).filter(([k]) => k !== col));
    });
  }, []);

  const handleClearFilter = useCallback((col: string) => {
    setColumnFilters((prev) => {
      const next = { ...prev };
      delete next[col];
      return next;
    });
  }, []);

  const handleSelectAll = useCallback((col: string, vals: string[]) => {
    if (vals.length === 0) return;
    setColumnFilters((prev) => ({ ...prev, [col]: vals }));
  }, []);

  // ── Sort ──────────────────────────────────────────────────────────────────
  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === "Asc" ? "Desc" : "Asc"));
    } else {
      setSortKey(key);
      setSortDir("Asc");
    }
  };

  const toggleExpand = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // ── Export ────────────────────────────────────────────────────────────────
  const handleExport = useCallback(async () => {
    if (!queueMicrosoftId || !startDate || !endDate) return;
    setExportLoading(true);
    try {
      // Export scopes to the queue/time range only — the table's own column filters and
      // agent search are intentionally ignored, while its active sort still applies.
      const result = await fetchQueueCallJourneyTable({
        startTime: startDate,
        endTime: endDate,
        queueMicrosoftId,
        sorts: sortKey ? [{ column: sortKey, direction: sortDir }] : null,
        pageIndex: 0,
        pageSize: null,
        filters: null,
        mainFilters: null,
      });
      const headers = EXPORT_COLUMNS.map((c) => c.title);
      const rows = result.data.map((row) =>
        EXPORT_COLUMNS.map((col) => {
          switch (col.key) {
            case "startTime":
              return convertLocalTimezone(row.startTime);
            case "voicemail":
              return row.voicemail ? "Yes" : "No";
            case "callDuration":
              return formatDuration(row.callDuration);
            case "waitingDuration":
              return formatDuration(row.waitingDuration);
            case "talkingDuration":
              return formatDuration(row.talkingDuration);
            case "direction":
              return formatDirectionForDisplay(row.direction);
            case "callStatus":
              return capitalizeFirst(row.callStatus);
            default:
              return (row as unknown as Record<string, unknown>)[col.key] ?? "";
          }
        })
      );
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      ws["!cols"] = EXPORT_COLUMNS.map(() => ({ wch: 15 }));
      XLSX.utils.book_append_sheet(wb, ws, "Call Journey");
      const d = new Date();
      const dateStr = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;
      const safeQueueName = (queueName || "queue").trim().replace(/\s+/g, "_");
      XLSX.writeFile(wb, `${safeQueueName}_calls_${dateStr}.csv`);
    } catch (e) {
      console.error("Queue call journey export failed:", e);
    } finally {
      setExportLoading(false);
    }
  }, [queueMicrosoftId, queueName, startDate, endDate, sortKey, sortDir]);


  // ── Table body ────────────────────────────────────────────────────────────
  const renderTableBody = () => {
    if (loading) {
      return skeletonRowIds.map((rowId) => (
        <div key={rowId} className={cn("table-row", ROW_HEIGHT_CLASS)}>
          <div className="table-cell px-1" style={{ width: 32 }} />
          {columns.map((col) => (
            <div key={col.key} className="table-cell px-4 align-middle" style={{ width: col.width }}>
              <div className="h-4 w-full bg-gray-200 animate-pulse rounded" />
            </div>
          ))}
        </div>
      ));
    }

    return tableData.map((row, idx) => {
      const expanded = expandedRows.has(row.id);
      const cellBorder = "border-b";
      return (
        <React.Fragment key={row.id}>
          <TableGridRow index={idx} className={cn(ROW_HEIGHT_CLASS, cellBorder)}>
            <TableGridCell
              index={idx}
              sticky
              className={cn(ROW_HEIGHT_CLASS, "px-1 border-l-0 border-r-0 border-gray-300 dark:border-0 z-20", cellBorder)}
              style={{ width: 32 }}
            >
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 hover:bg-action hover:text-white transition-all duration-200 flex items-center justify-center"
                onClick={() => toggleExpand(row.id)}
                aria-label={expanded ? "Collapse call journey" : "Expand call journey"}
              >
                {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </Button>
            </TableGridCell>
            {columns.map((col) => (
              <TableGridCell
                key={col.key}
                index={idx}
                className={cn(ROW_HEIGHT_CLASS, "text-xs text-primary dark:text-white border-gray-300 dark:border-0 transition-colors border-l-0 border-r-0", cellBorder)}
                style={{ width: col.width }}
              >
                <div className="text-left min-w-0">{getCellContent(col.key, row)}</div>
              </TableGridCell>
            ))}
          </TableGridRow>
          {expanded && (
            <tr className="table-row">
              {/* eslint-disable-next-line no-restricted-syntax -- expanded detail panel background, not zebra row/header coloring. */}
              <td className="table-cell align-middle py-1 text-muted-foreground bg-white dark:bg-[#3C5C73] dark:text-white overflow-hidden" colSpan={columns.length + 1}>
                {row.journey.length === 0 ? (
                  <div className="text-xs text-muted-foreground text-center">No journey events.</div>
                ) : (
                  <div className="w-full overflow-x-auto">
                    <Timeline events={row.journey} />
                  </div>
                )}
              </td>
            </tr>
          )}
        </React.Fragment>
      );
    });
  };

  // ── Cell rendering ──────────────────────────────────────────────────────────
  const getCellContent = (key: string, row: DirectCallJourneyRow) => {
    switch (key) {
      case "startTime":
        return <span className="text-xs">{convertLocalTimezone(row.startTime)}</span>;
      case "direction": {
        const label = formatDirectionForDisplay(row.direction);
        return <span className={cn("text-xs", getDirectionColor(label))}>{label}</span>;
      }
      case "callStatus": {
        const label = capitalizeFirst(row.callStatus);
        return <span className={cn("text-xs", getPresenceColor(label))}>{label || "-"}</span>;
      }
      case "callDuration":
        return <span className="text-xs">{formatDuration(row.callDuration)}</span>;
      case "voicemail":
        return (
          <span className="flex w-full pl-8">
            <Checkbox
              checked={row.voicemail === true}
              tabIndex={-1}
              className="h-5 w-5 rounded-md border-2 border-gray-300 dark:border-white/40 bg-transparent data-[state=checked]:bg-brand-accent data-[state=checked]:border-[#00FEC1] data-[state=checked]:text-black pointer-events-none cursor-default"
              aria-label={row.voicemail === true ? "Call forwarded to voice mail" : "Not forwarded to voice mail"}
            />
          </span>
        );
      case "organizer":
        return (
          <TruncatedTextTooltip content={row.organizer || "-"} className="text-xs">
            {row.organizer || "-"}
          </TruncatedTextTooltip>
        );
      case "participant":
        return (
          <TruncatedTextTooltip content={row.participant || "-"} className="text-xs">
            {row.participant || "-"}
          </TruncatedTextTooltip>
        );
      case "customerName":
        return (
          <TruncatedTextTooltip content={row.customerName || "-"} className="text-xs">
            {row.customerName || "-"}
          </TruncatedTextTooltip>
        );
      case "companyName":
        return (
          <TruncatedTextTooltip content={row.companyName || "-"} className="text-xs">
            {row.companyName || "-"}
          </TruncatedTextTooltip>
        );
      default:
        return <span className="text-xs">-</span>;
    }
  };

  return (
    // eslint-disable-next-line no-restricted-syntax -- Card container border, not table row/header coloring.
    <Card className={cn("flex flex-col dark:border-[#0C3451]", !collapsed && "h-full")}>
      <CardHeader className="p-0">
        <div className={cn("flex flex-row items-center justify-between gap-2 px-2 py-1.5", !collapsed && "border-b")}>
          <div className="flex items-center gap-2 min-w-0">
            <WidgetTitle className="truncate">{queueName ? `${queueName} - Call Journey` : "Queue Call Journey"}</WidgetTitle>
            <InfoTooltip content="The timeline of a call from the initial attempt to reach a Call Queue to the moment the call is terminated within the given time." />
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {!collapsed && (
              <>
                <SearchInput
                  placeholder="Search agent..."
                  onSearch={(v) => setSearch(v.trim())}
                  initialValue={search}
                  debounceMs={300}
                  inputClassName="h-6"
                />
                <Button
                  variant="outline"
                  // eslint-disable-next-line no-restricted-syntax -- Export button chrome, not table row/header coloring.
                  className="h-6 text-xs flex items-center gap-1 hover:bg-brand-accent hover:border-[#00FEC1] hover:text-black transition-colors dark:bg-[#0C3451] dark:border-[#4A6B85] dark:text-white dark:hover:bg-brand-accent dark:hover:border-[#00FEC1] dark:hover:text-black"
                  onClick={handleExport}
                  disabled={exportLoading}
                >
                  {exportLoading ? (
                    <>
                      <div className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      Exporting...
                    </>
                  ) : (
                    <>
                      <Download className="h-3 w-3" />
                      Export
                    </>
                  )}
                </Button>
              </>
            )}
            {onToggleCollapse && <CollapseToggleButton collapsed={collapsed} onToggle={onToggleCollapse} />}
          </div>
        </div>
      </CardHeader>
      {!collapsed && (
        <CardContent className="p-0 flex-1 flex flex-col min-h-0 overflow-hidden">
          <div className="flex-1 min-h-0 overflow-auto">
            <div className="min-w-full inline-block align-top">
              <div className="table w-full call-journey-table" style={{ borderCollapse: "collapse", borderSpacing: 0, tableLayout: "fixed" }}>
                <TableGridHeaderRow className={cn("text-xs", TABLE_HEADER_TITLE_CLASS)}>
                  <TableGridHeaderCell
                    sticky
                    className={cn(TABLE_HEADER_ROW_COLOR_CLASS, "h-8 px-1 text-center transition-colors z-40 font-bold border-t-0 border-l-0 border-r-0")}
                    style={{ width: 32 }}
                  />
                  {columns.map((col) => (
                    <TableGridHeaderCell
                      key={col.key}
                      className="h-8 border-b border-l-0 border-r-0 border-gray-300 dark:border-0 transition-colors"
                      style={{ width: col.width }}
                    >
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          className="flex items-center gap-1 cursor-pointer bg-transparent border-0 p-0 text-left"
                          onClick={() => handleSort(col.key)}
                        >
                          <span className={cn("whitespace-nowrap truncate", TABLE_HEADER_TITLE_CLASS)}>{col.title}</span>
                          {sortKey === col.key && (
                            sortDir === "Asc" ? <ChevronUp className="h-3 w-3 flex-shrink-0" /> : <ChevronDown className="h-3 w-3 flex-shrink-0" />
                          )}
                        </button>
                        {col.filterable && (
                          <ColFilterPopover
                            columnKey={col.key}
                            selectedValues={columnFilters[col.key] ?? []}
                            entry={colFilter.getEntry(col.key)}
                            onOpen={() => handleFilterOpen(col.key)}
                            onClose={() => handleFilterClose(col.key)}
                            onToggleValue={handleToggleFilterValue}
                            onSearchChange={handleFilterSearchChange}
                            onLoadMore={handleFilterLoadMore}
                            onClearFilter={handleClearFilter}
                            onSelectAll={handleSelectAll}
                          />
                        )}
                      </div>
                    </TableGridHeaderCell>
                  ))}
                </TableGridHeaderRow>
                <div className="table-row-group border-gray-300 dark:border-0">
                  {renderTableBody()}
                </div>
              </div>
              {!loading && (error || tableData.length === 0) && (
                <div className="py-12 text-center text-sm text-muted-foreground dark:text-white/60">
                  No call records were found
                </div>
              )}
            </div>
          </div>
          <TablePagingFooter
            currentPage={page}
            totalPages={Math.max(1, Math.ceil(total / limit))}
            total={total}
            pageSize={limit}
            onPageChange={setPage}
            isLoading={loading}
            pageSizeOptions={[10, 20, 50]}
            onPageSizeChange={(size) => { setLimit(size); setPage(1); }}
            startExtra={
              drilldownStatus && onClearDrilldown ? (
                <div className="flex shrink-0 items-center gap-2">
                  <span
                    className={cn(
                      "inline-flex h-7 shrink-0 items-center gap-1 whitespace-nowrap rounded-full border px-2.5 text-xs font-medium",
                      QUEUE_CALL_VOLUME_STATUS_COLOR_CLASS[drilldownStatus]
                    )}
                  >
                    {QUEUE_CALL_VOLUME_STATUS_LABEL[drilldownStatus]}
                    <button
                      type="button"
                      aria-label={`Remove ${QUEUE_CALL_VOLUME_STATUS_LABEL[drilldownStatus]} filter`}
                      onClick={onClearDrilldown}
                      className="rounded-full p-0.5 hover:bg-black/10 dark:hover:bg-white/20"
                    >
                      <X className="h-3 w-3" aria-hidden />
                    </button>
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    // eslint-disable-next-line no-restricted-syntax -- Export button chrome, not table row/header coloring.
                    className="h-8 shrink-0 gap-1 whitespace-nowrap text-xs flex items-center hover:bg-brand-accent hover:border-[#00FEC1] hover:text-black transition-colors dark:bg-[#3C5C73] dark:border-[#4A6B85] dark:text-white dark:hover:bg-brand-accent dark:hover:border-[#00FEC1] dark:hover:text-black"
                    onClick={onClearDrilldown}
                  >
                    <RefreshCcw className="h-3 w-3" aria-hidden />
                    Show all calls
                  </Button>
                </div>
              ) : undefined
            }
          />
        </CardContent>
      )}
    </Card>
  );
};

export default QueueCallJourneyTableWidget;
